<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'meterConsumptions',
    path: '/meter-consumptions',
    component: 'MeterConsumptionBrowser',
    title: () => gettext('Meter Consumptions'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'MeterConsumptionBrowser',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const onCreateInvoicesPayload = (...args) => {
    return { consumptions: _.map(selectedItems.value, 'id') };
  };
  const onGenerateMeterConsumptions = (...args) => {
    return $imodal.value
      .show({ name: 'GenerateMeterConsumptions' })
      .then((result) => {
        if (!result || result.cancel || !result.value) {
          return;
        }
        return result.value;
      });
  };

  const gridName = ref('meterConsumptions');
  const selectedItems = ref([]);
  const dsParams = ref({
    unbilled_only: true,
  });
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'invoice',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'Invoice'),
      sortPath: 'invoice.invoice_number',
      type: 'command',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <a href="#" onclick="return false;">\n\
      <strong title="{{ $value.invoice_number }}">{{ $value.invoice_number }}</strong>\n\
    </a>\n\
  {{~/if }}\n\
</span>\n\
',
          },
        ],
      },
    },
    {
      field: 'meter',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'Meter'),
      type: 'command',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong>{{ $value.serial_number }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'meter',
            navigateOnIconClick: true,
            params: {
              id: 'meter.id',
            },
          },
        ],
      },
    },
    {
      field: 'start.date',
      type: 'date',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'Start date'),
      sortPath: 'start.date',
      formatter: [
        {
          name: 'getvalue',
          opts: {
            field: 'start.date',
          },
        },
      ],
    },
    {
      field: 'start.reading',
      type: 'number',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'Start reading'),
      sortPath: 'start.reading',
      formatter: [
        {
          name: 'getvalue',
          opts: {
            field: 'start.reading',
          },
        },
        {
          name: 'number',
        },
      ],
    },
    {
      field: 'end.date',
      type: 'date',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'End date'),
      sortPath: 'end.date',
      formatter: [
        {
          name: 'getvalue',
          opts: {
            field: 'end.date',
          },
        },
      ],
    },
    {
      field: 'end.reading',
      type: 'number',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'End reading'),
      sortPath: 'end.reading',
      formatter: [
        {
          name: 'getvalue',
          opts: {
            field: 'end.reading',
          },
        },
        {
          name: 'number',
        },
      ],
    },
    {
      field: 'total_consumption',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'Total'),
    },
    {
      field: 'unit',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'Unit'),
    },
    {
      field: 'non_billable',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'Non-billable'),
      formatter: 'checkbox',
    },
    {
      field: 'rental',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'Rental'),
      type: 'command',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong>{{ $value.name }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'rental',
            navigateOnIconClick: true,
            params: {
              id: 'rental.id',
            },
          },
        ],
      },
    },
    {
      field: 'consumer',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'Consumer'),
      type: 'command',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong>{{ $value.code }} | {{ $value.name }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'consumer.id',
            },
          },
        ],
      },
    },
    {
      field: 'invoices',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'Invoices'),
      formatter: {
        name: 'chain',
        separator: ', ',
        formatterContext: 'item',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'invoice_number',
              source: 'value',
            },
          },
          {
            name: 'routeLink',
            to: 'invoice',
            navigateOnIconClick: true,
            params: {
              id: 'id',
            },
          },
        ],
      },
    },
  ]);

  const $data = computed(() => ({
    gridName: gridName.value,
    selectedItems: selectedItems.value,
    dsParams: dsParams.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = () => {
    return $fn.value.gridDataSource('integro_api/browse_meter_consumption', {
      parameters: () => ({
        ...dsParams.value,
        unbilled_only: dsParams.value.unbilled_only ? 1 : null,
      }),
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'gridName' },
      { name: 'selectedItems' },
      {
        name: 'dsParams',
        cache: { type: 'local', key: 'MeterConsumptionBrowser.dsParams' },
      },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      gridName,
      selectedItems,
      dsParams,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.gettext('Meter Consumptions'),
      icon: $config.value.meterConsumption.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_x0aehbkbx_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_upumkfucz_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2095089031469157_showModal_zip204h6k_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'UploadMeterReadings',
    };
  };
  const act_397195341670858_reloadGrid_zvsqfph59_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const inst_evh_397195341670858_close_l1fzpk6fn_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_397195341670858_showModal_k1zcw4703_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'MeterConsumption',
    };
  };
  const act_8823104369878821_reloadGrid_l6tct7bzz_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'meterConsumptions',
    };
  };
  const act_8823104369878821_request_amvipm0yp_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'bulk_delete_meter_consumption',
      data: {
        items: _.map(selectedItems.value, (p) => ({ id: p.id })),
      },
    };
  };

  const act_7315092382398562_closeModal_x0aehbkbx =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_x0aehbkbx_getActionArgs,
    });
  const inst_evh_7315092382398562_close_6hxisr3y4 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_x0aehbkbx,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_6hxisr3y4],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_2izpu8skx =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_tc9t76ali =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_2izpu8skx,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_tc9t76ali],
    event: 'reload',
  });
  const act_2248226175642056_setData_upumkfucz = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_upumkfucz_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_cmpte46j8 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_upumkfucz,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_cmpte46j8],
      event: 'selectedRowsChanged',
    });
  const act_2095089031469157_showModal_zip204h6k =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_zip204h6k_getActionArgs,
    });
  const inst_evh_2095089031469157_click_mhh8hkthi =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_zip204h6k,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2095089031469157_click_mhh8hkthi],
    event: 'click',
  });
  const act_397195341670858_reloadGrid_zvsqfph59 =
    new actionTypes.ReloadGridAction({
      actionArgs: act_397195341670858_reloadGrid_zvsqfph59_getActionArgs,
    });
  const inst_evh_397195341670858_close_l1fzpk6fn =
    new actionTypes.EventHandlerImpl({
      action: act_397195341670858_reloadGrid_zvsqfph59,
      event: 'close',
      when: inst_evh_397195341670858_close_l1fzpk6fn_getWhen,
      displayName: 'reloadGrid',
    });
  const act_397195341670858_showModal_k1zcw4703 =
    new actionTypes.ShowModalAction({
      actionArgs: act_397195341670858_showModal_k1zcw4703_getActionArgs,
      events: [inst_evh_397195341670858_close_l1fzpk6fn],
    });
  const inst_evh_397195341670858_click_p2v03n7a3 =
    new actionTypes.EventHandlerImpl({
      action: act_397195341670858_showModal_k1zcw4703,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_397195341670858_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_397195341670858_click_p2v03n7a3],
    event: 'click',
  });
  const act_8823104369878821_reloadGrid_l6tct7bzz =
    new actionTypes.ReloadGridAction({
      actionArgs: act_8823104369878821_reloadGrid_l6tct7bzz_getActionArgs,
    });
  const inst_evh_8823104369878821_success_7c9x7h4ut =
    new actionTypes.EventHandlerImpl({
      action: act_8823104369878821_reloadGrid_l6tct7bzz,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_8823104369878821_request_amvipm0yp = new actionTypes.RequestAction({
    actionArgs: act_8823104369878821_request_amvipm0yp_getActionArgs,
    displayName: 'deleteItems',
    events: [inst_evh_8823104369878821_success_7c9x7h4ut],
  });
  const inst_evh_8823104369878821_click_yhrkndrqh =
    new actionTypes.EventHandlerImpl({
      action: act_8823104369878821_request_amvipm0yp,
      event: 'click',
      displayName: 'deleteItems',
    });
  const inst_evh_8823104369878821_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8823104369878821_click_yhrkndrqh],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_6hxisr3y4,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_tc9t76ali,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_cmpte46j8,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2095089031469157_click_mhh8hkthi,
    inst_evh_2095089031469157_click,
    inst_evh_397195341670858_close_l1fzpk6fn,
    inst_evh_397195341670858_click_p2v03n7a3,
    inst_evh_397195341670858_click,
    inst_evh_8823104369878821_success_7c9x7h4ut,
    inst_evh_8823104369878821_click_yhrkndrqh,
    deleteItems: inst_evh_8823104369878821_click_yhrkndrqh,
    inst_evh_8823104369878821_click,
  };

  const evh_7315092382398562_close_6hxisr3y4 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_6hxisr3y4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_tc9t76ali = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_tc9t76ali.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_cmpte46j8 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_cmpte46j8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click_mhh8hkthi = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click_mhh8hkthi.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_397195341670858_close_l1fzpk6fn = async (event, scope) => {
    return await actions.inst_evh_397195341670858_close_l1fzpk6fn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_397195341670858_click_p2v03n7a3 = async (event, scope) => {
    return await actions.inst_evh_397195341670858_click_p2v03n7a3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_397195341670858_click = async (event, scope) => {
    return await actions.inst_evh_397195341670858_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8823104369878821_success_7c9x7h4ut = async (event, scope) => {
    return await actions.inst_evh_8823104369878821_success_7c9x7h4ut.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8823104369878821_click_yhrkndrqh = async (event, scope) => {
    return await actions.inst_evh_8823104369878821_click_yhrkndrqh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8823104369878821_click = async (event, scope) => {
    return await actions.inst_evh_8823104369878821_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="MeterConsumptionBrowser"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpDataGrid
      name="meterConsumptions"
      :headerTitle="pgettext('MeterConsumptionBrowser', 'Meter Consumptions')"
      :items="ds"
      checkbox-row-selection
      export-to-csv
      :exportToCsvFilename="
        pgettext('MeterConsumptionBrowser|ExportFilename', 'meter-consumptions')
      "
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2 align-items-center">
          <div>
            <ItpFormCheckbox
              name="unbilled_only"
              :label="pgettext('MeterConsumptionBrowser', 'Unbilled only')"
              inline
              v-model="dsParams.unbilled_only"
            >
            </ItpFormCheckbox>
          </div>
          <ItpButton
            :icon="$config.uploadIcon"
            :text="pgettext('MeterConsumptionBrowser', 'Upload readings...')"
            size="sm"
            variant="light"
            @click="evh_2095089031469157_click($event, {})"
          >
          </ItpButton>
          <JobHandlerControl
            jobType="generate_meter_consumptions"
            load-current
            :payload="onGenerateMeterConsumptions"
            icon="material-symbols:cloud-sync-outline-rounded"
            :text="
              pgettext(
                'GenerateMeterConsumptions|Submit',
                'Generate Consumptions'
              )
            "
            size="sm"
          >
          </JobHandlerControl>
          <ItpButton
            :icon="$config.meterConsumption.createMeterConsumptionIcon"
            :text="pgettext('MeterConsumptionBrowser', 'Add consumption...')"
            size="sm"
            variant="light"
            @click="evh_397195341670858_click($event, {})"
          >
          </ItpButton>
          <ItpButton
            icon="fa-trash"
            :title="pgettext('MeterConsumptionBrowser', 'Delete consumption')"
            :text="gettext('Delete')"
            size="sm"
            variant="light"
            :disabled="!selectedItems || !selectedItems.length"
            :spinning="actions.deleteItems.isRunning"
            @click="evh_8823104369878821_click($event, {})"
          >
          </ItpButton>
          <JobHandlerControl
            jobType="create_invoices_from_consumptions"
            load-current
            :payload="onCreateInvoicesPayload"
            icon="mdi:cog"
            :text="pgettext('MeterConsumptions|Submit', 'Generate Invoices')"
            :disabled="!selectedItems || !selectedItems.length"
            size="sm"
            class="ps-3 border-start"
          >
          </JobHandlerControl>
        </div>
      </template>
    </ItpDataGrid>
  </ItpPage>
</template>
